import { useMutation, useQuery } from '@apollo/client'
import { Edit } from '@mui/icons-material'
import {
    Box,
    Button,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { gql } from '__generated__'
import { format } from 'date-fns'
import { type FormikConfig, useFormik } from 'formik'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { ProfilePanel } from 'recruiter/components/ProfilePanel'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError, getFullName, getPxAsRem } from 'shared/utils'
import * as Yup from 'yup'

const GetCandidateCompensation = gql(/* GraphQL */ `
    query GetCandidateCompensation($candidateId: ID) {
        candidate(id: $candidateId) {
            id
            compensation {
                id
                compensation
                updatedByUser {
                    id
                    firstName
                    nickname
                    lastName
                }
                updatedAt
            }
        }
    }
`)

const UpdateCandidateCompensation = gql(/* GraphQL */ `
    mutation UpdateCandidateCompensation(
        $candidateId: ID!
        $compensation: String!
    ) {
        updateCandidateCompensation(
            candidateId: $candidateId
            compensation: $compensation
        ) {
            success
            message
        }
    }
`)

interface FormValues {
    compensation?: string
}

const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object({
    compensation: Yup.string(),
})

export function CandidateCompensationPanel() {
    const toast = useToast()
    const { candidateId } = useParams()
    const [isBeingEdited, setIsBeingEdited] = useState(false)
    const [auditString, setAuditString] = useState('')
    const [formValues, setFormValues] = useState<FormValues>({
        compensation: '',
    })

    const { loading: isLoading } = useQuery(GetCandidateCompensation, {
        fetchPolicy: 'cache-and-network',
        variables: { candidateId },
        onCompleted: (data) => {
            setFormValues({
                compensation: data.candidate?.compensation?.compensation || '',
            })

            const user = data.candidate?.compensation?.updatedByUser
            const auditDate = new Date(data.candidate?.compensation?.updatedAt)

            // If the `updatedAt` field is null, the audit date's value will be
            // "now" (epoch time 0). If it is undefined or another invalid date
            // string, the audit date's value will be NaN. Treat both of these
            // falsey values as indicating the value has never been set.
            if (!auditDate.valueOf()) return

            setAuditString(
                `Last Edited: ${format(auditDate, 'MM/dd/yyyy hh:mm a')} ${user ? getFullName(user) : 'Unknown User'}`
            )
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to fetch candidate compensation data'
                ),
            })
        },
    })

    const [updateCandidateCompensation] = useMutation(
        UpdateCandidateCompensation,
        {
            refetchQueries: [GetCandidateCompensation],
            onCompleted: (data) => {
                if (data.updateCandidateCompensation.success) {
                    toast({
                        title: 'Success!',
                        message:
                            'Candidate compensation has been updated successfully',
                        severity: 'success',
                    })
                } else {
                    toast({
                        title: 'Error',
                        message: `Failed to update candidate compensation: ${data.updateCandidateCompensation.message}`,
                        severity: 'error',
                    })
                }
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: formatGraphQLError(
                        error,
                        'Failed to update candidate compensation'
                    ),
                })
            },
        }
    )

    const handleEnableEditing = () => {
        setIsBeingEdited(true)
    }

    const handleUpdateCompensation: FormikConfig<FormValues>['onSubmit'] = (
        values
    ) => {
        updateCandidateCompensation({
            variables: {
                candidateId: candidateId || '',
                compensation: values.compensation || '',
            },
        })
        setFormValues(values)
        setIsBeingEdited(false)
    }

    const handleCancel = () => {
        formik.resetForm()
        setIsBeingEdited(false)
    }

    const formik = useFormik<FormValues>({
        enableReinitialize: true,
        initialValues: formValues,
        validationSchema,
        onSubmit: handleUpdateCompensation,
    })

    return (
        <ProfilePanel
            panelTitle="Compensation"
            panelSubtitle={auditString}
            actionElements={[
                <IconButton
                    key="edit"
                    onClick={handleEnableEditing}
                    disabled={isBeingEdited}
                >
                    <Edit />
                </IconButton>,
            ]}
            maxHeight="256px"
        >
            {isLoading ? (
                <Box flex="1">
                    <LoadingPanel />
                </Box>
            ) : isBeingEdited ? (
                <>
                    <TextField
                        autoFocus
                        error={
                            formik.touched.compensation &&
                            Boolean(formik.errors.compensation)
                        }
                        fullWidth
                        helperText={
                            formik.touched.compensation &&
                            formik.errors.compensation
                        }
                        label="Compensation"
                        multiline
                        name="compensation"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        rows="3"
                        value={formik.values.compensation}
                    />

                    <Stack direction="row" gap={getPxAsRem(8)}>
                        <Button variant="outlined" onClick={handleCancel}>
                            Cancel
                        </Button>

                        <Button
                            disabled={!formik.dirty}
                            onClick={formik.submitForm}
                        >
                            Save & Update
                        </Button>
                    </Stack>
                </>
            ) : (
                <Box overflow="auto">
                    <Typography>{formik.values.compensation || '-'}</Typography>
                </Box>
            )}
        </ProfilePanel>
    )
}
